import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, Users, Clock, UserX, ShieldX, Link2Off, LogIn } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useProjects } from '../context/ProjectContext';
import { apiPost } from '../utils/api';
import { toast } from 'react-hot-toast';
import { API_URL } from '../utils/config';
import { getToken as getAuthToken } from '../stores/authStore';

const AcceptInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { fetchProjects } = useProjects();

  const [status, setStatus] = useState('loading');
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const run = async () => {
      const isLoggedIn = !!getAuthToken();

      // If logged in, skip the invitation preview — accept directly
      if (isLoggedIn) {
        if (!cancelled) setStatus('accepting');

        try {
          const response = await apiPost(`/invitations/${token}/accept`);
          if (cancelled) return;

          if (response.ok) {
            const data = await response.json();
            setStatus('success');
            toast.success(data.message || 'Successfully joined project!');
            await fetchProjects();
            setTimeout(() => navigate(`/dashboard/projects/${data.project.id}`), 1000);
          } else {
            const data = await response.json();
            // If already accepted, redirect to the project if we can find it
            if (data.error?.toLowerCase().includes('already')) {
              toast.success('You are already a member of this project');
              await fetchProjects();
              // Try to find the project from the invitation data
              if (data.project?.id) {
                navigate(`/dashboard/projects/${data.project.id}`);
              } else {
                navigate('/dashboard');
              }
            } else {
              setStatus('error');
              setError(data.error || 'Failed to accept invitation');
            }
          }
        } catch (err) {
          if (!cancelled) {
            setStatus('error');
            setError('Failed to accept invitation');
          }
        }
        return;
      }

      // Not logged in — load invitation details to show preview
      try {
        const response = await fetch(`${API_URL}/invitations/${token}`);
        if (cancelled) return;

        if (!response.ok) {
          const data = await response.json();
          setStatus('error');
          setError(data.error || 'Invalid or expired invitation');
          return;
        }

        const data = await response.json();
        setInvitation(data.invitation);
        setStatus('ready');
      } catch (err) {
        if (!cancelled) {
          setStatus('error');
          setError('Failed to load invitation');
        }
      }
    };

    run();
    return () => { cancelled = true; };
  }, [token]);

  const handleAccept = () => {
    // Store redirect so login brings them back here. Stamp the invite timestamp
    // too, or clearStaleInvitationFlags wipes the redirect as stale during auth teardown.
    localStorage.setItem('jpro_login_redirect', `/invitations/${token}`);
    localStorage.setItem('jpro_invite_flag_ts', Date.now().toString());
    navigate('/login', { state: { from: { pathname: `/invitations/${token}` } } });
  };

  if (status === 'loading' || status === 'accepting') {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <Loader2 className={`w-12 h-12 animate-spin mx-auto mb-4 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-500'}`} />
          <p className={`font-rasa ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {status === 'accepting' ? 'Joining project...' : 'Loading invitation...'}
          </p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    const errorLower = error.toLowerCase();
    let ErrorIcon = XCircle;
    let errorTitle = 'Invitation Error';
    let errorHint = null;

    if (errorLower.includes('expired')) { ErrorIcon = Clock; errorTitle = 'Invitation Expired'; errorHint = 'Ask the project owner to send a new invitation.'; }
    else if (errorLower.includes('already') && errorLower.includes('accepted')) { ErrorIcon = CheckCircle; errorTitle = 'Already Accepted'; errorHint = 'You may already be a member of this project.'; }
    else if (errorLower.includes('declined')) { ErrorIcon = UserX; errorTitle = 'Invitation Declined'; errorHint = 'This invitation was previously declined.'; }
    else if (errorLower.includes('revoked') || errorLower.includes('cancelled')) { ErrorIcon = ShieldX; errorTitle = 'Invitation Revoked'; errorHint = 'The project owner cancelled this invitation.'; }
    else if (errorLower.includes('locked') || errorLower.includes('attempts')) { ErrorIcon = ShieldX; errorTitle = 'Invitation Locked'; errorHint = 'Too many attempts. Contact the project owner.'; }
    else if (errorLower.includes('login') || errorLower.includes('sign in') || errorLower.includes('authenticate')) { ErrorIcon = LogIn; errorTitle = 'Login Required'; errorHint = 'Please log in first, then try the link again.'; }
    else if (errorLower.includes('different') || errorLower.includes('wrong') || errorLower.includes('another account')) { ErrorIcon = UserX; errorTitle = 'Wrong Account'; errorHint = 'This invitation was sent to a different email.'; }
    else if (errorLower.includes('invalid') || errorLower.includes('not found')) { ErrorIcon = Link2Off; errorTitle = 'Invalid Link'; errorHint = 'This link is not valid. Check the URL.'; }
    else if (errorLower.includes('network') || errorLower.includes('connection') || errorLower.includes('failed to load')) { ErrorIcon = XCircle; errorTitle = 'Connection Error'; errorHint = 'Check your internet connection and try again.'; }

    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center max-w-md mx-auto p-6">
          <ErrorIcon className={`w-16 h-16 mx-auto mb-4 ${ErrorIcon === CheckCircle ? 'text-blue-500' : 'text-red-500'}`} />
          <h1 className={`text-2xl font-dm-serif mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{errorTitle}</h1>
          <p className={`font-rasa mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{error}</p>
          {errorHint && <p className={`font-rasa text-sm mb-6 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>{errorHint}</p>}
          <button onClick={() => navigate('/dashboard')} className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-rasa font-semibold transition-colors">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center max-w-md mx-auto p-6">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className={`text-2xl font-dm-serif mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Welcome to the project!</h1>
          <p className={`font-rasa ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Redirecting you now...</p>
        </div>
      </div>
    );
  }

  // Ready — show invitation details (only for NOT logged-in users)
  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className={`w-full max-w-md rounded-lg shadow-xl p-8 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="text-center mb-6">
          <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
            <Users className={`w-8 h-8 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
          </div>
          <h1 className={`text-2xl font-dm-serif mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>You're Invited!</h1>
        </div>

        <div className="mb-6">
          <p className={`text-center font-rasa mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            <strong>{invitation.invited_by}</strong> invited you to join:
          </p>
          <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
            <h2 className={`font-dm-serif text-lg font-semibold mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {invitation.project_name}
            </h2>
            {invitation.project_description && (
              <p className={`text-sm font-rasa ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{invitation.project_description}</p>
            )}
          </div>
        </div>

        <div className={`mb-6 p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-750' : 'bg-gray-50'}`}>
          <p className={`text-sm font-rasa mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            You'll join as a <strong className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{invitation.role}</strong>
          </p>
          <p className={`text-xs font-rasa ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
            Expires: {new Date(invitation.expires_at).toLocaleDateString()}
          </p>
        </div>

        <button
          onClick={handleAccept}
          className="w-full py-3 rounded-lg font-rasa font-semibold mb-3 transition-colors bg-blue-500 hover:bg-blue-600 text-white"
        >
          Log in to Accept
        </button>

        <button
          onClick={() => navigate('/')}
          className={`w-full py-2 rounded-lg font-rasa transition-colors ${
            theme === 'dark' ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-750' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          Decline
        </button>
      </div>
    </div>
  );
};

export default AcceptInvitation;
